import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { from, of, Subject } from 'rxjs';
import { catchError, debounceTime, map, switchMap } from 'rxjs/operators';

import { ApiService } from 'src/app/infrastructure/services/api-service/api.service';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';
import { AutocompleteControlComponent } from 'src/app/features/shared/controls/container/autocomplete-control/autocomplete-control.component';
import { ButtonActionComponent } from 'src/app/features/shared/controls/container/button-action/button-action.component';
import { ButtonsRowComponent } from 'src/app/features/shared/controls/container/buttons-row/buttons-row.component';
import { ChipsControlComponent, TChipItem } from 'src/app/features/shared/controls/container/chips-control/chips-control.component';
import { IconComponent } from 'src/app/features/shared/icons/container/icon.component';
import { InputControlComponent } from 'src/app/features/shared/controls/container/input-control/input-control.component';
import { TextareaControlComponent } from 'src/app/features/shared/controls/container/textarea-control/textarea-control.component';
import { buttonsRowAlign } from 'src/app/features/shared/controls/models/buttons-row-options';
import { IChatCandidateDto, chatCandidateDto } from 'src/assets/common/models/chat-candidate-dto.g';
import { IChatDto } from 'src/assets/common/models/chat-dto.g';

export interface IChatCreateDialogData {
    projectAMemberGuid: string | undefined;
    // Chat in een taak (fase 3c): de taak; kandidaten met de taakleden bovenaan.
    taskGuid?: string | undefined;
}

// Optie in de autocomplete: naam + e-mail; buitenstaanders (geen projectlid) krijgen een markering.
type TCandidateOption = { guid: string; label: string; email: string | null; isProjectMember: boolean };

// Nieuwe chat in het project: onderwerp, deelnemers (chips; kandidaten = actieve leden van het domain, projectleden
// eerst, via GET /api/Chat/participant-candidates) en het eerste bericht. De aanroeper doet altijd mee. ✓ maakt de
// chat aan (POST /api/Chat/create) en sluit met de nieuwe chat; bij een fout blijft de dialoog open (de melding
// komt van de centrale foutafhandeling). undefined = annuleren.
@Component({
    selector: 'app-chat-create-dialog',
    templateUrl: './chat-create-dialog.component.html',
    styleUrls: ['./chat-create-dialog.component.scss'],
    standalone: true,
    imports: [
        AutocompleteControlComponent,
        ButtonActionComponent,
        ButtonsRowComponent,
        ChipsControlComponent,
        IconComponent,
        InputControlComponent,
        TextareaControlComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatCreateDialogComponent
    implements OnInit
{
    private readonly __apiService = injectRequired(ApiService);
    private readonly __destroyRef = inject(DestroyRef);
    private readonly __dialogRef = inject<MatDialogRef<ChatCreateDialogComponent, IChatDto>>(MatDialogRef);
    private readonly __data = inject<IChatCreateDialogData>(MAT_DIALOG_DATA);
    private readonly __search$ = new Subject<void>();

    readonly buttonsRowAlign = buttonsRowAlign;
    readonly optionField = { display: 'label', value: 'guid' };

    readonly options$$ = signal<TCandidateOption[]>([]);
    readonly participants$$ = signal<TCandidateOption[]>([]);
    readonly chips$$ = computed<TChipItem[]>(() => this.participants$$().map(p => ({
        key: p.guid,
        label: p.isProjectMember ? p.label : `${p.label} ${this._text.outsider_suffix}`,
        icon: 'user',
    })));
    readonly saving$$ = signal<boolean>(false);
    private readonly __valid$$ = signal<boolean>(false);
    readonly canConfirm$$ = computed(() => this.__valid$$() && !this.saving$$());

    control_subject = new FormControl<string | null>(null, [Validators.required, Validators.maxLength(200)]);
    control_body = new FormControl<string | null>(null, [Validators.required, Validators.maxLength(4000)]);
    control_search = new FormControl<string | null>(null);

    protected readonly _text = {
        title: 'Nieuwe chat',
        subject_label: 'subject',
        participants_label: 'participants',
        participants_empty: 'alleen jij; projectleden doen mee zodra ze reageren',
        search_label: 'add participant',
        search_placeholder: 'search',
        outsider_suffix: '(buiten project)',
        body_label: 'message',
        remove: 'verwijderen',
        cancel: 'Annuleren',
        confirm: 'Chat starten',
    };

    ngOnInit(): void {
        this.control_subject.valueChanges.pipe(takeUntilDestroyed(this.__destroyRef)).subscribe(() => this.__valid_sync());
        this.control_body.valueChanges.pipe(takeUntilDestroyed(this.__destroyRef)).subscribe(() => this.__valid_sync());
        this.control_search.valueChanges.pipe(takeUntilDestroyed(this.__destroyRef)).subscribe(() => this.__search$.next());

        this.__search$.pipe(
            debounceTime(250),
            switchMap(() => this.__options_get$()),
            takeUntilDestroyed(this.__destroyRef),
        ).subscribe(list => this.options$$.set(list));

        this.__search$.next();
    }

    emit_optionSelected(option: TCandidateOption): void {
        // Het zoekveld maakt de control zelf leeg (clearOnSelect); dat start ook een nieuwe zoekvraag zonder deze persoon.
        if (!this.participants$$().some(p => p.guid === option.guid)) this.participants$$.update(list => [...list, option]);
    }

    emit_chipRemove(chip: TChipItem): void {
        this.participants$$.update(list => list.filter(p => p.guid !== chip.key));
        this.__search$.next();
    }

    emit_cancel(): void {
        this.__dialogRef.close(undefined);
    }

    emit_confirm(): void {
        if (!this.canConfirm$$()) return;
        this.saving$$.set(true);

        from(this.__apiService.POST('/api/Chat/create', {
            params: { query: { projectAMemberGuid: this.__data.projectAMemberGuid } },
            body: {
                subject: this.control_subject.value?.trim() ?? '',
                body: this.control_body.value?.trim() ?? '',
                participant_Guids: this.participants$$().map(p => p.guid),
                task_Guid: this.__data.taskGuid ?? null,
            },
        })).subscribe({
            next: r => {
                this.saving$$.set(false);
                const __dto = (r.data as any)?.modelDto as IChatDto | undefined;
                if (r.error || !__dto) return;
                this.__dialogRef.close(__dto);
            },
            error: () => this.saving$$.set(false),
        });
    }

    private __valid_sync(): void {
        this.__valid$$.set(!!this.control_subject.value?.trim() && !!this.control_body.value?.trim()
            && this.control_subject.valid && this.control_body.valid);
    }

    private __options_get$() {
        const __filter = this.control_search.value?.trim() || undefined;

        return from(this.__apiService.GET('/api/Chat/participant-candidates', {
            params: { query: { filter: __filter, projectAMemberGuid: this.__data.projectAMemberGuid, taskGuid: this.__data.taskGuid } },
        })).pipe(
            map(r => ((r.data as any)?.list ?? []) as IChatCandidateDto[]),
            // Al gekozen deelnemers niet nog eens aanbieden.
            map(list => list
                .filter(c => !this.participants$$().some(p => p.guid === c[chatCandidateDto.domainUser_Guid]))
                .map(c => ({
                    guid: c[chatCandidateDto.domainUser_Guid] ?? '',
                    label: c[chatCandidateDto.displayName] ?? '',
                    email: c[chatCandidateDto.email] ?? null,
                    isProjectMember: c[chatCandidateDto.isProjectMember] ?? false,
                }))),
            catchError(() => of([] as TCandidateOption[])),
        );
    }
}
